import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ReactComponent as AiIcon } from 'assets/icons/ai.svg';
import { ReactComponent as FileUploadIcon } from 'assets/icons/download.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import AICreatorBoMUploadModal from 'components/AICreatorBoMUploadModal/AICreatorBoMUploadModal';
import AiSubScreen from 'components/AiSubScreen/AiSubScreen';
import Button from 'components/Button/Button';
import Backdrop from 'components/Modal/Backdrop';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import {
  DEFAULT_SOCKET_FALLBACK_DELAY,
  VALUE_CHAIN_AI_AGENT,
} from 'constants/general';
import { BUTTON_VARIANT, DELETE_MODAL_TITLES, VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import useDropdownToggle from 'hooks/useDropdownToggle';
import useReviewTransitionLoading from 'hooks/useReviewTransitionLoading';
import useSessionUnitsPollingState from 'hooks/useSessionUnitsPollingState';
import DeleteModal from 'pages/CompanyAdminPages/ValueChainMap/components/DeleteModal/DeleteModal';
import { useGetProductCategoriesQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import {
  useExploreAgentSessionBomAiMutation,
  useExploreAgentSessionBomAllTabsAiMutation,
  useUpdateAgentSessionBomSelectionMutation,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';
import {
  getBomLineItemsNotFilled,
  getUnitIdFromNodeId,
  isAIAgentSessionReviewReadyStatus,
  isVcAgentBomSocketProcessingStatus,
  normalizeTabStatus,
} from 'utils/AICreatorHelper';
import { showBadRequestError } from 'utils/helpers';
import { getSessionTemplateTitle } from 'utils/sessionTemplateTitle';

import UnitLinkField from '../UnitLinkField/UnitLinkField';
import BomUnitsTable from './components/BomUnitsTable/BomUnitsTable';
import BomUnitsTabs from './components/BomUnitsTabs/BomUnitsTabs';
import useAICreatorBoMBomItem from './hooks/useAICreatorBoMBomItem';
import useAICreatorBoMManufacturers from './hooks/useAICreatorBoMManufacturers';
import useAICreatorBoMRoot from './hooks/useAICreatorBoMRoot';
import useAICreatorBoMTable from './hooks/useAICreatorBoMTable';
import useAICreatorBoMTabs from './hooks/useAICreatorBoMTabs';

import type { IAICardSelection } from 'components/AICardPanel/interfaces/IAICardSelection';
import type IUpdateAgentSessionBomItemBody from 'store/slices/valueChainMap/interfaces/IUpdateAgentSessionBomItemBody';
import type {
  EVCAgentUnitStatus,
  IVCAgentSessionUnitUpdatedPayload,
} from '../../hooks/interfaces/IUseWebSocket';
import type IUnitLinkItem from '../UnitLinkField/interfaces/IUnitLinkItem';
import type { TBomFocusCell } from './components/RowAICreatorBoM/interfaces/IRowAICreatorBoM';
import type IAICreatorBoMUnits from './interfaces/IAICreatorBoMUnits';

import styles from './sass/AICreatorBoMUnits.module.scss';

function AICreatorBoMUnits({
  open,
  selectedCards,
  availableManufacturerCards,
  session,
  onClose,
  onGenerateWithAI,
  onAddRecord,
  onAddManufacturer,
  onConfirm,
  initialActiveTabNodeId,
  handleCloseModalBom,
  onProcessingTabsChange,
  isBomSocketConnected,
  sendBomSocketUnitUpdated,
  lastBomSocketPayload,
  isSocketPollingFallbackActive,
}: IAICreatorBoMUnits) {
  const { t } = useTranslation();

  const [bomErrorFocusCell, setBomErrorFocusCell] = useState<TBomFocusCell | null>(null);
  const [focusPendingTabNodeId, setFocusPendingTabNodeId] = useState<string | null>(null);
  const [bomRowPendingDeleteId, setBomRowPendingDeleteId] = useState<number | null>(null);
  const [bomAiSocketPayload, setBomAiSocketPayload] = useState<IVCAgentSessionUnitUpdatedPayload>();
  const [pendingAiUnitIds, setPendingAiUnitIds] = useState<number[]>([]);
  const [isLocalSocketFallbackActive, setIsLocalSocketFallbackActive] = useState(false);
  const [isConfirmAllLoading, setIsConfirmAllLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const prevCurrentBomIdRef = useRef<number>(0);
  const prevPollingSelectedUnitsKeyRef = useRef<string>('');
  const sentBomAiSocketPayloadRef = useRef<string>('');
  const confirmErrorToastIdRef = useRef<ReturnType<typeof toast.error> | null>(null);
  const pendingAiFallbackTimerRef = useRef<number | null>(null);
  const wasPendingAiUnitIdsEmptyRef = useRef(true);

  const {
    isReviewTransitionLoading,
    startReviewTransitionLoading,
    clearReviewTransitionLoading,
    finishReviewTransitionLoading,
  } = useReviewTransitionLoading();

  const {
    isOpen: isGenerateDropdownOpen,
    ref: generateDropdownRef,
    toggle: toggleGenerateDropdown,
    close: closeGenerateDropdown,
  } = useDropdownToggle();

  const { data: productCategories } = useGetProductCategoriesQuery();
  const [exploreAgentSessionBomAi] = useExploreAgentSessionBomAiMutation();
  const [exploreAgentSessionBomAllTabsAi] = useExploreAgentSessionBomAllTabsAiMutation();
  const [
    updateAgentSessionBomSelection,
    { isLoading: isUpdateAgentSessionBomSelectionLoading },
  ] = useUpdateAgentSessionBomSelectionMutation();

  const {
    valueChainMapId,
    naceCodesData,
    agentSessionBom,
    isAgentSessionBomFetching,
    refetchAgentSessionBom,
    sessionUnitStatusByUnitId,
    updateAgentSessionBomItem,
    deleteAgentSessionBomItem,
    isDeleteAgentSessionBomItemLoading,
    createAgentSessionBomItem,
    updateAgentSessionBom,
    startAgentSessionReview,
    isStartAgentSessionReviewLoading,
  } = useAICreatorBoMRoot({ session });

  const {
    tabs,
    setTabs,
    activeTabNodeId,
    setActiveTabNodeId,
    isSelectAllChecked,
    setIsSelectAllChecked,
    activeTab,
    activeTabIndex,
    isCreateAction,
    syncCurrentBomStatusInProgress,
  } = useAICreatorBoMTabs({
    open,
    selectedCards,
    session,
    sessionUnitStatusByUnitId,
    valueChainMapId,
    sessionId: session?.id,
    updateAgentSessionBom,
    pendingAiUnitIds,
    initialActiveTabNodeId,
    onBadRequestError: showBadRequestError,
  });

  const {
    currentBomId,
    agentSessionBomItem,
    isAgentSessionBomItemFetching,
  } = useAICreatorBoMBomItem({
    session,
    valueChainMapId,
    activeTabNodeId,
    tabs,
    agentSessionBom,
  });

  const {
    tableItems,
    setTableItems,
    onLoadMore,
    hasMore,
    isAgentSessionBomItemsFetching,
    refetchAgentSessionBomItems,
  } = useAICreatorBoMTable({
    valueChainMapId,
    sessionId: session?.id,
    currentBomId,
    listContextKey: activeTabNodeId,
    open,
  });
  const activeTabUnitId = activeTab ? getUnitIdFromNodeId(activeTab.nodeId) : null;
  const isBomTabProcessing = activeTab?.status === VALUE_CHAIN_AI_AGENT.AI_STATUS;

  const handleBlockedProcessingEdit = useCallback(() => {
    toast.warning(t('notifications.valueChainBomUnitsProcessingEditBlocked'), {
      toastId: 'value-chain-bom-units-processing-edit-blocked',
    });
  }, [t]);

  const {
    pollingSelectedUnitsKey,
    hasPollingSessionProcessingUnits,
    selectedUnitStatusByUnitId,
  } = useSessionUnitsPollingState(session?.selectedUnits);

  useEffect(() => {
    if (!lastBomSocketPayload || !session?.id || lastBomSocketPayload.sessionId !== session.id) {
      return;
    }
    const matchedTabUnitId = lastBomSocketPayload.unitId;

    setTabs((prevTabs) => prevTabs.map((tab) => {
      const tabUnitId = getUnitIdFromNodeId(tab.nodeId);

      if (tabUnitId !== matchedTabUnitId) {
        return tab;
      }

      return {
        ...tab,
        status: normalizeTabStatus(lastBomSocketPayload.status),
      };
    }));

    if (!isVcAgentBomSocketProcessingStatus(lastBomSocketPayload.status)) {
      setPendingAiUnitIds((prev) => prev.filter((id) => id !== matchedTabUnitId));
    }

    Promise.resolve(refetchAgentSessionBom()).catch(() => {});

    if (open && activeTabUnitId !== null && activeTabUnitId === matchedTabUnitId) {
      refetchAgentSessionBomItems();
    }
  }, [
    lastBomSocketPayload,
    session?.id,
    open,
    activeTabUnitId,
    refetchAgentSessionBom,
    refetchAgentSessionBomItems,
    setTabs,
  ]);

  const {
    selectedManufacturerCards,
    availableActiveManufacturerCards,
    flushPendingManufacturerSync,
    handleManufacturerDropdownOpen,
    handleManufacturerCardToggle,
    handleManufacturerCardRemove,
  } = useAICreatorBoMManufacturers({
    open,
    currentBomId,
    availableManufacturerCards,
    producedByUnits: agentSessionBomItem?.data?.producedByUnits,
    valueChainMapId,
    session,
    activeTab,
    updateAgentSessionBom,
    onAddManufacturer,
    isManufacturerEditingDisabled: isBomTabProcessing,
    onBlockedManufacturerEdit: handleBlockedProcessingEdit,
    onBadRequestError: showBadRequestError,
  });

  // Manufacturer cards are unique per node, so the node id doubles as the chip identity.
  const toUnitLinkItem = (card: IAICardSelection) => ({
    itemKey: card.nodeId,
    name: card.name,
    type: card.type,
  });

  const selectedManufacturerItems = useMemo(
    () => selectedManufacturerCards.map(toUnitLinkItem),
    [selectedManufacturerCards],
  );

  const manufacturerOptions = useMemo(
    () => availableActiveManufacturerCards.map(toUnitLinkItem),
    [availableActiveManufacturerCards],
  );

  const manufacturerCardsByNodeId = useMemo(
    () => new Map(availableActiveManufacturerCards.map((card) => [card.nodeId, card])),
    [availableActiveManufacturerCards],
  );

  const handleManufacturerItemToggle = useCallback((item: IUnitLinkItem) => {
    const card = manufacturerCardsByNodeId.get(item.itemKey);

    if (!card) {
      return;
    }

    handleManufacturerCardToggle({
      ...card,
      status: VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT,
    });
  }, [handleManufacturerCardToggle, manufacturerCardsByNodeId]);

  const handleManufacturerPickerClose = useCallback(() => {
    flushPendingManufacturerSync().catch(() => undefined);
  }, [flushPendingManufacturerSync]);

  const naceOptions = naceCodesData;
  const categoryOptions = useMemo(
    () => productCategories?.data?.items.map((category) => ({
      label: category.name,
      value: `${category.id}`,
    })) ?? [],
    [productCategories],
  );

  const emptyStateMessage = useMemo(() => {
    const socketMessage = lastBomSocketPayload?.message?.trim();
    const socketUnitId = lastBomSocketPayload?.unitId;

    if (
      socketMessage
      && activeTab?.name
      && activeTabUnitId !== null
      && socketUnitId === activeTabUnitId
    ) {
      return t('valueChainMap.aiGeneratingNothingFound', {
        name: activeTab.name,
      });
    }

    return t('valueChainMap.bomUnitsEmptyState', {
      name: activeTab?.name || '',
    });
  }, [
    activeTab?.name,
    activeTabUnitId,
    lastBomSocketPayload?.message,
    lastBomSocketPayload?.unitId,
    t,
  ]);

  const loading = isAgentSessionBomFetching
    || isAgentSessionBomItemFetching
    || isAgentSessionBomItemsFetching
    || isStartAgentSessionReviewLoading;
  const isSessionReviewReady = isAIAgentSessionReviewReadyStatus(session?.status);
  const isReviewAction = isCreateAction || isSessionReviewReady;
  const isFooterActionDisabled = isSessionReviewReady
    ? isAgentSessionBomFetching || !tabs.length
    : isAgentSessionBomFetching
      || !tabs.length
      || tableItems.length === 0
      || isBomTabProcessing;

  const pendingAiUnitIdsSet = useMemo(() => new Set(pendingAiUnitIds), [pendingAiUnitIds]);

  const tabsForIndicator = useMemo(() => {
    if (pendingAiUnitIdsSet.size === 0) {
      return tabs;
    }

    return tabs.map((tab) => {
      const tabUnitId = getUnitIdFromNodeId(tab.nodeId);
      const isPending = tabUnitId !== null && pendingAiUnitIdsSet.has(tabUnitId);

      if (isPending && tab.status !== VALUE_CHAIN_AI_AGENT.AI_STATUS) {
        return { ...tab, status: VALUE_CHAIN_AI_AGENT.AI_STATUS };
      }

      return tab;
    });
  }, [pendingAiUnitIdsSet, tabs]);

  const isAnyTabAiProcessing = useMemo(
    () => tabs.some((tab) => tab.status === VALUE_CHAIN_AI_AGENT.AI_STATUS)
      || tabsForIndicator.some((tab) => tab.status === VALUE_CHAIN_AI_AGENT.AI_STATUS)
      || pendingAiUnitIds.length > 0
      || hasPollingSessionProcessingUnits,
    [hasPollingSessionProcessingUnits, pendingAiUnitIds.length, tabs, tabsForIndicator],
  );

  const isConfirmAllDisabled = isFooterActionDisabled || isConfirmAllLoading || isAnyTabAiProcessing;

  useEffect(() => {
    if (!onProcessingTabsChange) {
      return;
    }

    if (!open) {
      onProcessingTabsChange(false);
      return;
    }

    const hasProcessingTabs = tabs.some((tab) => (
      tab.status === VALUE_CHAIN_AI_AGENT.AI_STATUS
      || tab.status === VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS
    ));

    onProcessingTabsChange(hasProcessingTabs);
  }, [onProcessingTabsChange, open, tabs]);

  useEffect(() => {
    if (!open || pendingAiUnitIds.length === 0) {
      return;
    }

    const completedPendingUnitIds = pendingAiUnitIds.filter((unitId) => {
      const liveStatus = sessionUnitStatusByUnitId.get(unitId) ?? selectedUnitStatusByUnitId.get(unitId);

      return Boolean(liveStatus) && !isVcAgentBomSocketProcessingStatus(liveStatus);
    });

    if (completedPendingUnitIds.length === 0) {
      return;
    }

    setPendingAiUnitIds((prev) => prev.filter((unitId) => !completedPendingUnitIds.includes(unitId)));

    if (currentBomId && activeTabUnitId !== null && completedPendingUnitIds.includes(activeTabUnitId)) {
      refetchAgentSessionBomItems();
    }
  }, [
    activeTabUnitId,
    currentBomId,
    open,
    pendingAiUnitIds,
    refetchAgentSessionBomItems,
    selectedUnitStatusByUnitId,
    sessionUnitStatusByUnitId,
  ]);

  useEffect(() => {
    if (pendingAiUnitIds.length === 0) {
      if (pendingAiFallbackTimerRef.current !== null) {
        window.clearTimeout(pendingAiFallbackTimerRef.current);
        pendingAiFallbackTimerRef.current = null;
      }

      wasPendingAiUnitIdsEmptyRef.current = true;
      setIsLocalSocketFallbackActive(false);
      return;
    }

    if (!wasPendingAiUnitIdsEmptyRef.current) {
      return;
    }

    wasPendingAiUnitIdsEmptyRef.current = false;

    pendingAiFallbackTimerRef.current = window.setTimeout(() => {
      pendingAiFallbackTimerRef.current = null;
      setIsLocalSocketFallbackActive(true);
    }, DEFAULT_SOCKET_FALLBACK_DELAY);
  }, [pendingAiUnitIds]);

  useEffect(() => () => {
    if (pendingAiFallbackTimerRef.current !== null) {
      window.clearTimeout(pendingAiFallbackTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!open || (!isSocketPollingFallbackActive && !isLocalSocketFallbackActive)) {
      return undefined;
    }

    const hasProcessingTabs = tabs.some((tab) => tab.status === VALUE_CHAIN_AI_AGENT.AI_STATUS)
      || hasPollingSessionProcessingUnits
      || pendingAiUnitIds.length > 0;

    if (!hasProcessingTabs) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      refetchAgentSessionBom();

      if (currentBomId) {
        refetchAgentSessionBomItems();
      }
    }, 10000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    currentBomId,
    hasPollingSessionProcessingUnits,
    isLocalSocketFallbackActive,
    isSocketPollingFallbackActive,
    open,
    pendingAiUnitIds.length,
    refetchAgentSessionBom,
    refetchAgentSessionBomItems,
    tabs,
  ]);

  useEffect(() => {
    const prevKey = prevPollingSelectedUnitsKeyRef.current;
    prevPollingSelectedUnitsKeyRef.current = pollingSelectedUnitsKey;

    if (
      !open
      || !isSocketPollingFallbackActive
      || pendingAiUnitIds.length === 0
      || !session?.selectedUnits?.length
      || !prevKey
      || prevKey === pollingSelectedUnitsKey
    ) {
      return;
    }

    const pollingSelectedUnitStatusByUnitId = new Map(
      session.selectedUnits.map((selectedUnit) => [selectedUnit.unitId, selectedUnit.status] as const),
    );
    const completedPendingUnitIds = pendingAiUnitIds.filter((unitId) => !isVcAgentBomSocketProcessingStatus(
      pollingSelectedUnitStatusByUnitId.get(unitId),
    ));

    if (completedPendingUnitIds.length === 0) {
      return;
    }

    setPendingAiUnitIds((prev) => prev.filter((unitId) => !completedPendingUnitIds.includes(unitId)));

    Promise.resolve(refetchAgentSessionBom()).catch(() => {});

    if (
      currentBomId
      && (
        !hasPollingSessionProcessingUnits
        || (activeTabUnitId !== null && completedPendingUnitIds.includes(activeTabUnitId))
      )
    ) {
      refetchAgentSessionBomItems();
    }
  }, [
    activeTabUnitId,
    currentBomId,
    hasPollingSessionProcessingUnits,
    isSocketPollingFallbackActive,
    open,
    pendingAiUnitIds,
    pollingSelectedUnitsKey,
    refetchAgentSessionBom,
    refetchAgentSessionBomItems,
    session?.selectedUnits,
  ]);

  useEffect(() => {
    const isBomIdStable = prevCurrentBomIdRef.current === currentBomId;
    prevCurrentBomIdRef.current = currentBomId;

    if (!isBomIdStable) {
      return;
    }

    if (!focusPendingTabNodeId || activeTab?.nodeId !== focusPendingTabNodeId) {
      return;
    }

    if (isAgentSessionBomItemsFetching || isAgentSessionBomItemFetching) {
      return;
    }

    const notFilledBomLines = getBomLineItemsNotFilled(tableItems);
    const firstNotFilled = notFilledBomLines[0];
    const firstNotFilledItem = firstNotFilled ? tableItems[firstNotFilled.lineIndex] : null;

    if (firstNotFilled && firstNotFilledItem) {
      setBomErrorFocusCell({
        itemId: firstNotFilledItem.id,
        field: firstNotFilled.missingFields[0],
        focusKey: Date.now(),
      });
    }

    setFocusPendingTabNodeId(null);
  }, [
    activeTab?.nodeId,
    currentBomId,
    focusPendingTabNodeId,
    isAgentSessionBomItemFetching,
    isAgentSessionBomItemsFetching,
    tableItems,
  ]);

  useEffect(() => {
    if (!open) {
      setBomAiSocketPayload(undefined);
      sentBomAiSocketPayloadRef.current = '';
      prevPollingSelectedUnitsKeyRef.current = '';
      setPendingAiUnitIds([]);
      clearReviewTransitionLoading();
      confirmErrorToastIdRef.current = null;
    }
  }, [clearReviewTransitionLoading, open]);

  useEffect(() => {
    if (!bomAiSocketPayload) {
      sentBomAiSocketPayloadRef.current = '';
      return;
    }

    if (!isBomSocketConnected) {
      return;
    }

    const payloadKey = JSON.stringify(bomAiSocketPayload);
    if (sentBomAiSocketPayloadRef.current === payloadKey) {
      return;
    }

    const isSent = sendBomSocketUnitUpdated(bomAiSocketPayload);
    if (isSent) {
      sentBomAiSocketPayloadRef.current = payloadKey;
    }
  }, [bomAiSocketPayload, isBomSocketConnected, sendBomSocketUnitUpdated]);

  useEffect(() => {
    setIsSelectAllChecked(tableItems.some((tableItem) => tableItem.isActive));
  }, [setIsSelectAllChecked, tableItems]);

  const handleSelectAllChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!valueChainMapId || !session?.id || !currentBomId) {
      return;
    }

    const nextIsActive = event.target.checked;

    try {
      await updateAgentSessionBomSelection({
        valueChainMapId,
        sessionId: session.id,
        bomId: currentBomId,
        body: {
          isActive: nextIsActive,
        },
      }).unwrap();
    } catch (error) {
      showBadRequestError(error);
      return;
    }

    setTableItems((prev) => prev.map((tableItem) => (
      tableItem.isActive !== nextIsActive
        ? { ...tableItem, isActive: nextIsActive }
        : tableItem
    )));
    setIsSelectAllChecked(nextIsActive);
  };

  const handleCreateRecord = async () => {
    if (!valueChainMapId || !session?.id || !currentBomId) {
      return;
    }

    try {
      const response = await createAgentSessionBomItem({
        valueChainMapId,
        sessionId: session.id,
        bomId: currentBomId,
      }).unwrap();

      if (response?.data) {
        setTableItems((prev) => [response.data, ...prev.filter((tableItem) => tableItem.id !== response.data.id)]);
        await syncCurrentBomStatusInProgress(currentBomId, true);
      }
    } catch (error) {
      showBadRequestError(error);
      return;
    }

    onAddRecord();
  };

  const handleGenerateWithAI = async () => {
    if (!valueChainMapId || !session?.id || !currentBomId || !activeTab) {
      return;
    }

    if (selectedManufacturerCards.length === 0) {
      const noSelectionKey = activeTab.type === VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT
        ? 'notifications.valueChainBomUnitsNoManufacturerSelected'
        : 'notifications.valueChainBomUnitsNoSupplierSelected';
      toast.error(t(noSelectionKey));
      return;
    }

    const activeUnitId = getUnitIdFromNodeId(activeTab.nodeId);

    if (activeUnitId == null) {
      return;
    }

    const nextBomAiSocketPayload: IVCAgentSessionUnitUpdatedPayload = {
      sessionId: session.id,
      unitId: activeUnitId,
      status: VALUE_CHAIN_AI_AGENT.AI_STATUS as EVCAgentUnitStatus,
    };

    setPendingAiUnitIds((prev) => (prev.includes(activeUnitId) ? prev : [...prev, activeUnitId]));

    setBomAiSocketPayload(nextBomAiSocketPayload);

    try {
      await exploreAgentSessionBomAi({
        valueChainMapId,
        sessionId: session.id,
        bomId: currentBomId,
      }).unwrap();
      onGenerateWithAI();
    } catch (error) {
      setBomAiSocketPayload(undefined);
      if (activeUnitId != null) {
        setPendingAiUnitIds((prev) => prev.filter((id) => id !== activeUnitId));
      }
      showBadRequestError(error);
    }
  };

  const handleGenerateWithAIAllTabs = async () => {
    if (!valueChainMapId || !session?.id) {
      return;
    }

    closeGenerateDropdown();

    const tabsWithNoSelection = new Set(
      tabs
        .filter((tab) => {
          const tabUnitId = getUnitIdFromNodeId(tab.nodeId);
          const bom = agentSessionBom?.data?.find(
            (b) => tabUnitId !== null && b.sessionUnit.unitId === tabUnitId,
          );
          return !bom?.producedByUnits?.length;
        })
        .map((tab) => getUnitIdFromNodeId(tab.nodeId))
        .filter((id): id is number => id !== null),
    );

    if (tabsWithNoSelection.size > 0) {
      toast.warning(t('valueChainMap.bomUnitsNoSelectionForAllTabs'));
    }

    const allUnitIds = tabs
      .map((tab) => getUnitIdFromNodeId(tab.nodeId))
      .filter((unitId): unitId is number => unitId !== null && !tabsWithNoSelection.has(unitId));

    setPendingAiUnitIds((prev) => {
      const pendingSet = new Set(prev);
      allUnitIds.forEach((id) => pendingSet.add(id));
      return Array.from(pendingSet);
    });

    try {
      await exploreAgentSessionBomAllTabsAi({
        valueChainMapId,
        sessionId: session.id,
      }).unwrap();
      await new Promise<void>((resolve) => { window.setTimeout(resolve, 500); });
      Promise.resolve(refetchAgentSessionBom()).catch(() => {});
      onGenerateWithAI();
    } catch (error) {
      setPendingAiUnitIds((prev) => prev.filter((id) => !allUnitIds.includes(id)));
      showBadRequestError(error);
    }
  };

  const handleUpdateRow = async (itemId: number, body: IUpdateAgentSessionBomItemBody) => {
    if (!valueChainMapId || !session?.id || !currentBomId) {
      return;
    }

    try {
      const response = await updateAgentSessionBomItem({
        valueChainMapId,
        sessionId: session.id,
        bomId: currentBomId,
        itemId,
        body,
      }).unwrap();

      if (response?.data) {
        setTableItems((prev) => prev.map((tableItem) => (
          tableItem.id === itemId ? response.data : tableItem
        )));
        await syncCurrentBomStatusInProgress(currentBomId, true);
      }
    } catch (error) {
      showBadRequestError(error);
      throw error;
    }
  };

  const handleDeleteRow = async (itemId: number) => {
    if (!valueChainMapId || !session?.id || !currentBomId) {
      return;
    }

    try {
      await deleteAgentSessionBomItem({
        valueChainMapId,
        sessionId: session.id,
        bomId: currentBomId,
        itemId,
      }).unwrap();
    } catch (error) {
      showBadRequestError(error);
      throw error;
    }

    setTableItems((prev) => prev.filter((tableItem) => tableItem.id !== itemId));
    const hasItemsAfterDelete = tableItems.some((tableItem) => tableItem.id !== itemId);
    await syncCurrentBomStatusInProgress(currentBomId, hasItemsAfterDelete);
  };

  const handleRequestDeleteRow = (itemId: number) => {
    if (isBomTabProcessing) {
      handleBlockedProcessingEdit();
      return;
    }

    setBomRowPendingDeleteId(itemId);
  };

  const handleRejectDeleteRow = () => {
    setBomRowPendingDeleteId(null);
  };

  const handleConfirmDeleteRow = async () => {
    if (bomRowPendingDeleteId == null) {
      return;
    }

    if (isBomTabProcessing) {
      handleBlockedProcessingEdit();
      setBomRowPendingDeleteId(null);
      return;
    }

    const itemId = bomRowPendingDeleteId;

    try {
      await handleDeleteRow(itemId);
      setBomRowPendingDeleteId(null);
    } catch {
      // Keep modal open so the user can retry after a failed request.
    }
  };

  const handleClose = async () => {
    try {
      await flushPendingManufacturerSync();
    } catch {
      return;
    }

    setIsUploadModalOpen(false);
    onClose();
  };

  const handleUploadModalOpen = useCallback(() => {
    if (!currentBomId || isBomTabProcessing) {
      return;
    }

    setIsUploadModalOpen(true);
  }, [currentBomId, isBomTabProcessing]);

  const handleUploadModalClose = useCallback(() => {
    setIsUploadModalOpen(false);
  }, []);

  const handleUploadImported = useCallback(() => {
    setBomErrorFocusCell(null);
    refetchAgentSessionBom();
    refetchAgentSessionBomItems();
  }, [refetchAgentSessionBom, refetchAgentSessionBomItems]);

  const handleStartReview = async (): Promise<boolean> => {
    if (!valueChainMapId || !session?.id) {
      return false;
    }

    try {
      await startAgentSessionReview({
        valueChainMapId,
        sessionId: session.id,
      }).unwrap();
      onConfirm();
      handleCloseModalBom();

      return true;
    } catch (error) {
      confirmErrorToastIdRef.current = showBadRequestError(error)
        ?? toast.error(t('notifications.somethingWentWrong'));

      return false;
    }
  };

  const handleConfirmAll = async () => {
    if (!valueChainMapId || !session?.id || isAnyTabAiProcessing) {
      return;
    }

    if (isSessionReviewReady) {
      onConfirm();
      handleCloseModalBom();
      return;
    }

    setIsConfirmAllLoading(true);

    const incompleteTabs = tabs.filter(
      (tab) => tab.status !== VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT,
    );

    const incompleteTabsWithBom = incompleteTabs.flatMap((tab) => {
      const tabUnitId = getUnitIdFromNodeId(tab.nodeId);
      const bom = agentSessionBom?.data?.find(
        (b) => tabUnitId !== null && b.sessionUnit.unitId === tabUnitId,
      );

      return bom ? [bom] : [];
    });

    let hasError = false;

    await incompleteTabsWithBom.reduce(async (previousPromise, bom) => {
      await previousPromise;

      if (hasError) {
        return;
      }

      try {
        await updateAgentSessionBom({
          valueChainMapId: valueChainMapId!,
          sessionId: session!.id,
          bomId: bom.id,
          body: { status: VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT },
        }).unwrap();
      } catch (error) {
        showBadRequestError(error);
        hasError = true;
      }
    }, Promise.resolve());

    if (hasError) {
      setIsConfirmAllLoading(false);
      return;
    }

    setTabs((prev) => prev.map((tab) => ({
      ...tab,
      status: VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT,
    })));

    await handleStartReview();
    setIsConfirmAllLoading(false);
  };

  const handleConfirm = async () => {
    if (!valueChainMapId || !session?.id) {
      return;
    }

    if (confirmErrorToastIdRef.current !== null) {
      toast.dismiss(confirmErrorToastIdRef.current);
      confirmErrorToastIdRef.current = null;
    }

    try {
      await flushPendingManufacturerSync();
    } catch {
      return;
    }

    if (isSessionReviewReady) {
      onConfirm();
      handleCloseModalBom();
      return;
    }

    if (isCreateAction) {
      await handleStartReview();
      return;
    }

    if (!currentBomId || !activeTab) {
      return;
    }

    const notFilledBomLines = getBomLineItemsNotFilled(tableItems);
    if (notFilledBomLines.length) {
      const firstNotFilled = notFilledBomLines[0];
      const firstNotFilledItem = tableItems[firstNotFilled.lineIndex];

      if (firstNotFilledItem) {
        setBomErrorFocusCell({
          itemId: firstNotFilledItem.id,
          field: firstNotFilled.missingFields[0],
          focusKey: Date.now(),
        });
      }
      toast.error(t('notifications.valueChainBomUnitsLinesNotFilled'));
      return;
    }

    setBomErrorFocusCell(null);

    const isCompletingActiveTab = activeTab.status !== VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT;
    const nextTabsAfterConfirm = isCompletingActiveTab
      ? tabs.map((tab) => (
        tab.nodeId === activeTab.nodeId
          ? { ...tab, status: VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT }
          : tab
      ))
      : tabs;
    const firstIncompleteTab = nextTabsAfterConfirm.find(
      (tab) => tab.status !== VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT,
    );
    const nextTab = firstIncompleteTab ? undefined : nextTabsAfterConfirm[activeTabIndex + 1];
    const nextActiveTabIndex = nextTab ? activeTabIndex + 1 : activeTabIndex;
    const shouldDelayReviewTransition = !firstIncompleteTab
      && !isCompletingActiveTab
      && nextTabsAfterConfirm.length > 0
      && nextActiveTabIndex === nextTabsAfterConfirm.length - 1
      && nextTabsAfterConfirm.every(
        (tab) => tab.status === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT,
      );
    const reviewTransitionStartedAt = shouldDelayReviewTransition
      ? startReviewTransitionLoading()
      : null;

    if (isCompletingActiveTab) {
      try {
        await updateAgentSessionBom({
          valueChainMapId,
          sessionId: session.id,
          bomId: currentBomId,
          body: {
            status: VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT,
          },
        }).unwrap();
      } catch (error) {
        clearReviewTransitionLoading();
        confirmErrorToastIdRef.current = showBadRequestError(error)
          ?? toast.error(t('notifications.somethingWentWrong'));
        return;
      }
    }

    if (firstIncompleteTab) {
      if (isCompletingActiveTab) {
        setTabs(nextTabsAfterConfirm);
      }

      setBomErrorFocusCell(null);
      setActiveTabNodeId(firstIncompleteTab.nodeId);
      setFocusPendingTabNodeId(firstIncompleteTab.nodeId);
      return;
    }

    if (isCompletingActiveTab) {
      setTabs(nextTabsAfterConfirm);
      await handleStartReview();
      return;
    }

    if (nextTab) {
      if (isCompletingActiveTab) {
        setTabs(nextTabsAfterConfirm);
      }

      await finishReviewTransitionLoading(reviewTransitionStartedAt);
      setActiveTabNodeId(nextTab.nodeId);
      return;
    }

    await finishReviewTransitionLoading(reviewTransitionStartedAt);

    if (isCompletingActiveTab) {
      setTabs(nextTabsAfterConfirm);
    }
  };

  if (!open) {
    return null;
  }

  const bomModalTitle = (() => {
    const templateTitle = getSessionTemplateTitle(session);
    const baseTitle = t('valueChainMap.bomUnitsModalTitle');

    return templateTitle ? `${baseTitle} · ${templateTitle}` : baseTitle;
  })();

  return createPortal(
    <>
      {/* Declared to the assistant under the title the modal shows. The tab goes in the data it
          sends, never in the composer: a session unit is (sessionId, unitId), and the agent
          session tools take a tab by its unitId. */}
      <AiSubScreen
        name={bomModalTitle}
        customData={{
          sessionId: session?.id,
          subType: VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE,
          activeTab: activeTabUnitId === null ? null : { unitId: activeTabUnitId, name: activeTab?.name },
        }}
      />
      <Backdrop>
        <div className={styles.root} data-bom-units-modal>
          <div className={styles.content}>
            <div className={styles.header}>
              <h2 className={styles.title}>{bomModalTitle}</h2>
              <div className={styles.actions}>
                <div ref={generateDropdownRef} className={styles.generateWrapper}>
                  <Button
                    title={t('valueChainMap.bomUnitsGenerateWithAI')}
                    variant={BUTTON_VARIANT.primary}
                    startIcon={<AiIcon />}
                    className={styles.actionButton}
                    onClick={toggleGenerateDropdown}
                    disabled={isBomTabProcessing}
                  />
                  {isGenerateDropdownOpen ? (
                    <div className={styles.generateDropdown}>
                      <button
                        type="button"
                        className={styles.generateDropdownItem}
                        onClick={handleGenerateWithAIAllTabs}
                      >
                        {t('valueChainMap.bomUnitsGenerateWithAIForAllItems')}
                      </button>
                      <button
                        type="button"
                        className={styles.generateDropdownItem}
                        onClick={() => {
                          closeGenerateDropdown();
                          handleGenerateWithAI();
                        }}
                      >
                        {t('valueChainMap.bomUnitsGenerateWithAIForCurrentOnly')}
                      </button>
                    </div>
                  ) : null}
                </div>
                <Button
                  title={t('valueChainMap.bomUnitsUploadFile')}
                  variant={BUTTON_VARIANT.primary}
                  startIcon={<FileUploadIcon />}
                  className={styles.actionButton}
                  onClick={handleUploadModalOpen}
                  disabled={!currentBomId || isBomTabProcessing}
                />
                <Button
                  title={t('valueChainMap.bomUnitsAddRecord')}
                  variant={BUTTON_VARIANT.primary}
                  startIcon={<PlusIcon />}
                  className={styles.actionButton}
                  onClick={handleCreateRecord}
                  disabled={isBomTabProcessing}
                />
              </div>
            </div>
            <BomUnitsTabs
              open={open}
              tabs={tabsForIndicator}
              activeNodeId={activeTab?.nodeId ?? ''}
              onTabSelect={setActiveTabNodeId}
            />
            <UnitLinkField
              // Remounts per BoM item so a switch cannot carry the open picker across.
              key={currentBomId}
              label={VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT === activeTab?.type
                ? t('valueChainMap.bomUnitsManufacturedBy')
                : t('valueChainMap.bomUnitsDeliveredToPrefix')}
              items={selectedManufacturerItems}
              options={manufacturerOptions}
              onToggle={handleManufacturerItemToggle}
              onRemove={handleManufacturerCardRemove}
              isEditDisabled={isBomTabProcessing}
              onBlockedEdit={handleBlockedProcessingEdit}
              onPickerOpen={handleManufacturerDropdownOpen}
              onPickerClose={handleManufacturerPickerClose}
              boundarySelector="[data-bom-units-modal]"
            />
            <BomUnitsTable
              open={open}
              isAnalysisInProgress={isBomTabProcessing}
              isSelectAllLoading={isUpdateAgentSessionBomSelectionLoading}
              isSelectAllChecked={isSelectAllChecked}
              onSelectAllChange={handleSelectAllChange}
              tableItems={tableItems}
              naceOptions={naceOptions}
              categoryOptions={categoryOptions}
              onUpdate={handleUpdateRow}
              onDelete={handleRequestDeleteRow}
              onLoadMore={onLoadMore}
              hasMore={hasMore}
              emptyStateMessage={emptyStateMessage}
              focusCell={bomErrorFocusCell ?? undefined}
              onStartEdit={() => setBomErrorFocusCell(null)}
            />
          </div>
          <div className={styles.footer}>
            <Button
              title={t('common.cancel')}
              variant={BUTTON_VARIANT.cancel}
              className={styles.footerButton}
              onClick={handleClose}
            />
            <div className={styles.footerConfirmGroup}>
              <Button
                title={t('common.confirmAll')}
                variant={BUTTON_VARIANT.cancel}
                className={styles.footerButton}
                onClick={handleConfirmAll}
                disabled={isConfirmAllDisabled}
                isLoading={isConfirmAllLoading}
                loadingIndicator={<SmallLoader size={18} />}
              />
              <Button
                title={isReviewAction ? t('common.review') : t('common.confirm')}
                variant={BUTTON_VARIANT.primary}
                className={styles.footerButton}
                onClick={handleConfirm}
                disabled={isFooterActionDisabled}
                isLoading={loading || isReviewTransitionLoading}
                loadingIndicator={<SmallLoader size={18} />}
              />
            </div>
          </div>
        </div>
      </Backdrop>
      <DeleteModal
        open={bomRowPendingDeleteId != null}
        onReject={handleRejectDeleteRow}
        isLoading={isDeleteAgentSessionBomItemLoading}
        name=""
        title={DELETE_MODAL_TITLES.DELETE_CONFIRMATION}
        onConfirm={handleConfirmDeleteRow}
      />
      <AICreatorBoMUploadModal
        open={isUploadModalOpen}
        valueChainMapId={valueChainMapId || ''}
        sessionId={session?.id}
        bomId={currentBomId}
        onClose={handleUploadModalClose}
        onImported={handleUploadImported}
      />
    </>,
    document.body,
  );
}

export default AICreatorBoMUnits;
